import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

// Catches errors thrown while rendering a route — most importantly a lazy
// page chunk that fails to load (e.g. right after a deploy, when the old
// hashed chunk file no longer exists). Without this, React unmounts the whole
// tree and the user is left with a blank screen.
//
// A chunk-load failure reloads the page once, which picks up the new
// index.html and its new chunk names. sessionStorage remembers that reload so
// a chunk that still fails afterwards shows a message instead of looping.
// Any other error never auto-reloads; it just shows the message.

const RELOAD_KEY = 'devstackkit:chunk-reload-at';
// A later failure outside this window (say, after another deploy in the same
// tab) gets its own automatic reload.
const RELOAD_WINDOW_MS = 60_000;

const CHUNK_ERROR_MESSAGE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading (CSS )?chunk .* failed/i;

export function isChunkLoadError(error: unknown): boolean {
  // Parcel loads a page's CSS chunk through a <link> and rejects with the
  // link's error Event (not an Error) when it fails.
  if (typeof Event !== 'undefined' && error instanceof Event) {
    return error.target instanceof HTMLLinkElement || error.target instanceof HTMLScriptElement;
  }
  if (error instanceof Error) {
    return error.name === 'ChunkLoadError' || CHUNK_ERROR_MESSAGE.test(error.message);
  }
  return false;
}

// Records the reload and returns true if one is allowed now. Returns false if
// one already happened recently, or if sessionStorage is unavailable (without
// it there is no way to stop a reload loop).
function claimReload(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY));
    if (last && Date.now() - last < RELOAD_WINDOW_MS) return false;
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

type Phase = 'ok' | 'pending' | 'reloading' | 'failed';

interface BoundaryProps {
  resetKey: string;
  children: ReactNode;
}

interface BoundaryState {
  phase: Phase;
  chunkError: boolean;
}

class RouteErrorBoundaryInner extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { phase: 'ok', chunkError: false };

  static getDerivedStateFromError(error: unknown): BoundaryState {
    const chunkError = isChunkLoadError(error);
    // A chunk error stays on the spinner ('pending') until componentDidCatch
    // decides whether to reload, so the message doesn't flash before a reload.
    return { phase: chunkError ? 'pending' : 'failed', chunkError };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error('Route failed to render:', error, info.componentStack);
    if (this.state.phase !== 'pending') return;
    if (claimReload()) {
      this.setState({ phase: 'reloading' });
      window.location.reload();
    } else {
      this.setState({ phase: 'failed' });
    }
  }

  componentDidUpdate(prevProps: BoundaryProps) {
    // Navigating elsewhere (sidebar, header search) clears the error, so the
    // rest of the site stays usable after one page fails.
    if (prevProps.resetKey !== this.props.resetKey && this.state.phase === 'failed') {
      this.setState({ phase: 'ok', chunkError: false });
    }
  }

  render() {
    const { phase, chunkError } = this.state;
    if (phase === 'ok') return this.props.children;

    if (phase === 'pending' || phase === 'reloading') {
      return (
        <div className="route-loading" role="status" aria-label="Loading">
          <div className="route-loading-spinner" />
        </div>
      );
    }

    return (
      <div className="route-error" role="alert">
        <div className="route-error-card">
          <h1>{chunkError ? "This page couldn't load" : 'Something went wrong'}</h1>
          <p>
            {chunkError
              ? 'The site may have just been updated, or your connection dropped. Reloading usually fixes it.'
              : 'This page ran into an unexpected error. Reloading may fix it.'}
          </p>
          <button type="button" className="route-error-button" onClick={() => window.location.reload()}>
            Reload page
          </button>
        </div>
      </div>
    );
  }
}

const RouteErrorBoundary = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  return <RouteErrorBoundaryInner resetKey={pathname}>{children}</RouteErrorBoundaryInner>;
};

export default RouteErrorBoundary;
